import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ROLE_SCOPE, type Role } from "@workcore/schemas";
import { api, checkedWrite } from "../api";
import { KIND_LABEL, parsePrincipal, useContainers, useDefinitions, useDirectory, useRefresh, useRoles } from "../directory";
import { ErrorBox, PageHeader, Panel, SidePanel } from "../ui";
import { HistoryLink } from "../activity";
import { confirmAction } from "../confirm";

const ROLES: { role: Role; label: string; what: string }[] = [
  { role: "system_admin", label: "System admin", what: "users, groups, service accounts, which definitions and projects exist; no project content" },
  { role: "workflow_admin", label: "Workflow admin", what: "edits one workflow definition and its templates" },
  { role: "project_admin", label: "Project admin", what: "sees everything in one project and manages its access" },
  { role: "member", label: "Member", what: "works on objects they can see" },
  { role: "guest", label: "Guest", what: "reads and comments only" },
];

const GRANTABLE = ROLES.filter((r) => r.role !== "member" && r.role !== "guest");
const BASE = new Set<Role>(["member", "guest"]);

export function RolesPage() {
  const { slug = "" } = useParams();
  const roles = useRoles(slug);
  const dir = useDirectory(slug);
  const defs = useDefinitions(slug).data ?? [];
  const containers = (useContainers(slug).data ?? []).filter((c) => !c.isTemplate);
  const refresh = useRefresh(slug);
  const [principal, setPrincipal] = useState("");
  const [role, setRole] = useState<Role>("workflow_admin");
  const [scopeId, setScopeId] = useState("");
  const [adding, setAdding] = useState(false);
  const scopeKind = ROLE_SCOPE[role];
  const scopeName = (kind: string, id: number | null) =>
    id === null ? "the workspace" : kind === "definition" ? `${defs.find((d) => d.id === id)?.name ?? `workflow ${id}`} (workflow)` : `${containers.find((c) => c.id === id)?.name ?? `project ${id}`} (project)`;

  const grant = useMutation({
    mutationFn: () => checkedWrite("POST", `/v1/ws/${slug}/roles`, { principal: parsePrincipal(principal), role, ...(scopeKind === "workspace" ? {} : { scopeId: Number(scopeId) }) }),
    onSuccess: () => (setPrincipal(""), setScopeId(""), setAdding(false), refresh()),
  });
  const revoke = useMutation({ mutationFn: (id: number) => api("DELETE", `/v1/ws/${slug}/roles/${id}`), onSuccess: refresh });
  // one row per person (or group, or service account): their base role, and every admin role with its scope
  const label = (r: Role) => ROLES.find((x) => x.role === r)?.label ?? r;
  const order: Role[] = ["system_admin", "workflow_admin", "project_admin"];
  const people = [...(roles.data ?? []).reduce((m, r) => {
    const key = `${r.principal.kind}:${r.principal.id}`;
    const e = m.get(key) ?? { key, principal: r.principal, base: undefined as (typeof r) | undefined, admin: [] as (typeof r)[] };
    if (BASE.has(r.role)) e.base = r;
    else e.admin.push(r);
    m.set(key, e);
    return m;
  }, new Map<string, { key: string; principal: NonNullable<typeof roles.data>[number]["principal"]; base: NonNullable<typeof roles.data>[number] | undefined; admin: NonNullable<typeof roles.data>[number][] }>()).values()]
    .map((e) => ({ ...e, admin: e.admin.sort((a, b) => order.indexOf(a.role) - order.indexOf(b.role) || scopeName(a.scopeKind, a.scopeId).localeCompare(scopeName(b.scopeKind, b.scopeId))) }))
    .sort((a, b) => dir.label(a.principal).localeCompare(dir.label(b.principal)));

  return (
    <>
      <PageHeader title="Roles" description="Who administers what. Everyone also has one base role, member or guest, set when they are invited." actions={<><HistoryLink slug={slug} kind="roles" /><button onClick={() => setAdding(true)}>Grant a role</button></>} />
      <SidePanel title="Grant a role" open={adding} onClose={() => setAdding(false)} onSubmit={() => grant.mutate()} submitLabel="Grant" busy={grant.isPending || !principal} error={grant.error}>
        <div className="sentence">
          <span>Grant</span>
          <select aria-label="Who" required value={principal} autoFocus onChange={(e) => setPrincipal(e.target.value)}>
            <option value="">someone…</option>
            {dir.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span>the</span>
          <select aria-label="Role" value={role} onChange={(e) => (setRole(e.target.value as Role), setScopeId(""))}>
            {/* member/guest is each user's single base role, set by invitation, not granted here */}
            {GRANTABLE.map((r) => <option key={r.role} value={r.role}>{r.label}</option>)}
          </select>
          <span>role{scopeKind === "workspace" ? " for the whole workspace." : " on"}</span>
          {scopeKind === "definition" && (
            <select aria-label="Workflow" required value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
              <option value="">which workflow…</option>
              {defs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          {scopeKind === "container" && (
            <select aria-label="Project" required value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
              <option value="">which project…</option>
              {containers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
        <p className="muted small">{ROLES.find((r) => r.role === role)?.label}: {ROLES.find((r) => r.role === role)?.what}.</p>
        {scopeKind === "container" && containers.length === 0 && <p className="muted small">No projects yet.</p>}
      </SidePanel>
      <Panel title="Assignments" plain>
        <ErrorBox error={roles.error ?? revoke.error} />
        <table className="grid">
          <thead><tr><th>Who</th><th>Base role</th><th>Administers</th></tr></thead>
          <tbody>
            {people.map(({ key, principal: p, base, admin }) => (
              <tr key={key}>
                <td>{dir.label(p)} <span className="muted small">{KIND_LABEL[p.kind]}</span></td>
                <td>{base ? <span className="badge" title="Each person has exactly one base role, member or guest. Change it on the Users page.">{label(base.role)}</span> : <span className="muted">—</span>}</td>
                <td>
                  {admin.length === 0 && <span className="muted">—</span>}
                  <div className="chips">
                    {admin.map((r) => {
                      const what = `${label(r.role)} on ${scopeName(r.scopeKind, r.scopeId)}`;
                      return (
                        <span className="chip" key={r.id}>
                          <span>{label(r.role)}</span>
                          <span className="chip-scope">{scopeName(r.scopeKind, r.scopeId)}</span>
                          <button
                            type="button"
                            className="chip-x"
                            aria-label={`Revoke ${what} from ${dir.label(p)}`}
                            title="Revoke"
                            onClick={() => void confirmAction({ title: `Revoke ${what} from ${dir.label(p)}?`, body: "It stops applying on their next request. You can grant it again.", action: "Revoke", check: () => api("DELETE", `/v1/ws/${slug}/roles/${r.id}`, { dryRun: true }) }).then((ok) => ok && revoke.mutate(r.id))}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </>
  );
}
